import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';

// filter paper types
const filterPaperOption = (option) =>
  option?.text &&
  !option.text.toLowerCase().includes('canvas') &&
  !option.text.toLowerCase().includes('deckle') &&
  !option.text.toLowerCase().includes('lustre');

const MountedPrintsFormInputs = ({ quantityRef }) => {
  const {
    form,
    setForm,
    resetPrintSizeForm,
    setPrintSizeLoading,
    processPrintSizeData,
    setFinishingServiceLoading,
    processFinishingCategoriesData,
    setFrameInputsLoading,
    processFinishingServicesData,
    processFinishingDetailsData,
    processFinishingServiceChange,
    getBaseMeta,
    resetFormInputs,
    getMetaResets,
  } = useProductSelectionStore();

  const selectRef = useRef(null);
  const paperTypeSelectedValue = form?.inputs?.paperType?.selectedValue;
  const printSizeSelectedValue = form?.inputs?.printSize?.selectedValue;
  const finishingCategoryId = form?.meta?.finishingCategoryId;
  const finishingServiceInput = form?.inputs?.finishingService;

  // --- Queries ---
  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processPrintSizeData(response);
    },
  });

  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (paperTypeSelectedValue && paperTypeSelectedValue !== 'default' && printSizesQuery.isLoading) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeSelectedValue],
    queryFn: () => httpClient.get(`products/print_sizes/${printSizeSelectedValue}/finishing_categories`),
    enabled: !!printSizeSelectedValue && printSizeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingCategoriesData(response);
    },
  });

  useEffect(() => {
    if (printSizeSelectedValue && printSizeSelectedValue !== 'default' && finishingCategoriesQuery.isLoading) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeSelectedValue, finishingCategoriesQuery.isLoading]);

  const finishingServicesQuery = useQuery({
    queryKey: ['finishingServices', printSizeSelectedValue, finishingCategoryId],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeSelectedValue}/finishing_categories/${finishingCategoryId}/finishing_services`
      ),
    enabled: !!printSizeSelectedValue && !!finishingCategoryId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingServicesData(response);
    },
  });

  useEffect(() => {
    if (finishingCategoryId && finishingCategoryId !== 'default' && finishingServicesQuery.isLoading) {
      setFrameInputsLoading(true);
    }
  }, [finishingCategoryId, finishingServicesQuery.isLoading]);

  useQuery({
    queryKey: ['finishingDetailsOptions', form?.meta?.finishingServiceId],
    queryFn: () => httpClient.get(`products/${form.meta.finishingServiceId}/finishing_details`),
    enabled: !!form?.meta?.finishingServiceId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingDetailsData(response);
    },
  });

  // Auto-select "Mounting" from finishingService options
  useEffect(() => {
    const options = finishingServiceInput?.options;
    if (!options || options.length === 0) return;
    if (finishingServiceInput?.selectedValue) return;

    const mountingOption = options.find((opt) => opt?.text === 'Mounting');
    if (!mountingOption) return;

    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        finishingService: {
          ...prev.inputs.finishingService,
          selectedValue: mountingOption.value,
          selectedValueText: mountingOption.text,
        },
      },
    }));
  }, [finishingServiceInput?.options, finishingServiceInput?.selectedValue, setForm]);

  useEffect(() => {
    if (finishingServiceInput?.selectedValueText === undefined) return;
    processFinishingServiceChange(finishingServiceInput.selectedValueText);
  }, [finishingServiceInput?.selectedValueText]);

  useEffect(() => {
    const mountColorValue = form?.inputs?.mountColor?.selectedValue;
    if (!mountColorValue || mountColorValue === 'default') return;

    setForm((prevState) => ({
      meta: {
        ...getBaseMeta(prevState),
        finishingDetailId: mountColorValue,
        requiredInputs: [],
      },
      inputs: prevState.inputs,
    }));
  }, [form?.inputs?.mountColor?.selectedValue]);

  // Handlers
  const handleInputChange = (key, e) => {
    const input = form?.inputs?.[key];
    if (!input) return;

    setForm((prevState) => {
      const resetInputs = resetFormInputs(prevState.inputs, key);

      resetInputs[key] = {
        ...resetInputs[key],
        selectedValue: e.target.value,
        selectedValueText: e.target.options[e.target.selectedIndex].text,
        selectedValuePrice: input.options.find(
          (option) => option?.value?.toString() === e.target.value
        )?.price,
      };

      const newMeta = { ...prevState.meta };
      const metaResets = getMetaResets(key, prevState);
      if (metaResets.finishingCategoryId) newMeta.finishingCategoryId = undefined;
      if (metaResets.finishingServiceId) newMeta.finishingServiceId = undefined;
      if (metaResets.finishingDetailId) {
        if (key === 'mountColor') {
          newMeta.finishingDetailId = e.target.value;
          newMeta.requiredInputs = [];
        } else {
          newMeta.finishingDetailId = undefined;
        }
      }

      if (input.options[0]?.xAspectRatio && input.options[0]?.yAspectRatio) {
        const selectedOption = input.options.find(
          (option) => option?.value?.toString() === e.target.value
        );
        newMeta.xAspectRatio = selectedOption?.xAspectRatio;
        newMeta.yAspectRatio = selectedOption?.yAspectRatio;
        newMeta.aspectRatio = selectedOption?.xAspectRatio / selectedOption?.yAspectRatio;
      }

      return { meta: newMeta, inputs: resetInputs };
    });
  };

  if (!form || !form.inputs) return null;

  const VISIBLE_KEYS = ['paperType', 'printSize', 'mountColor'];

  // Option filter per field
  const filterOptionsForKey = (option, key) => {
    if (key === 'paperType') return filterPaperOption(option);
    return true;
  };

  return (
    <div
      className={styles.printsFormStack}
      style={{ display: 'grid', gridTemplateColumns: '1fr', gridGap: '15px' }}
    >
      {/* Quantity */}
      <div className="input-container quantity" style={{ gridColumn: 'span 2' }}>
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {VISIBLE_KEYS.map((key) => {
        const input = form.inputs[key];
        if (!input) return null;
        return (
          <div key={key} style={input.style}>
            <div className="input-container paper-type">
              <div className="label-container">
                <div className="div-block-150">
                  <div className="sub-label-form">{input.label}</div>
                </div>
              </div>
              <div className="form-block-input w-form">
                <form className="form-input select-field">
                  <select
                    ref={selectRef}
                    className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(key, e)}
                    value={input.selectedValue || 'default'}
                    disabled={!!input.isDisabled}
                    title={
                      input.isDisabled && input.disabledMessage
                        ? input.disabledMessage
                        : undefined
                    }
                  >
                    {input.isLoading === true && (!input.options || input.options.length === 0) ? (
                      <option value="">Loading...</option>
                    ) : (
                      <option value="default">Choose One</option>
                    )}
                    {input.options && input.options.length > 0 &&
                      input.options
                        .filter((option) => !option.text?.endsWith('**'))
                        .filter((option) => filterOptionsForKey(option, key))
                        .map((option) => {
                          if (!option || !option.value || !option.text) return null;
                          const displayText = key === 'printSize'
                            ? option.text
                            : option.text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();
                          return (
                            <option key={option.value} value={option.value}>
                              {displayText}
                            </option>
                          );
                        })
                        .filter(Boolean)}
                  </select>
                  <img
                    src={ChevronIcon}
                    alt=""
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      width: '16px',
                      height: '16px',
                    }}
                  />
                </form>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MountedPrintsFormInputs;
